<template>
  <Head title="Log in" />

  <transition
    name="fade"
    mode="out-in"
  >
    <div v-if="!selectedCompany">
      <form
        @submit.prevent="submitSelectCompany"
        class="flex flex-col gap-y-5"
      >
        <BreezeLabel
          for="companyId"
          value="Organization Name"
          class="sr-only"
        />
        <div>
          <BreezeInput
            id="companyId"
            type="text"
            v-model="enteredCompanyName"
            autofocus
            autocomplete="off"
            autocapitalize="off"
            autocorrect="off"
            spellcheck="false"
            placeholder="Enter your organization..."
            class="text-center"
          />
        </div>

        <div class="flex justify-start">
          <BreezeButton class="w-full"> Continue </BreezeButton>
        </div>
      </form>
    </div>
    <div
      v-else
      class="flex flex-col gap-y-8"
    >
      <div v-if="hasAuth0">
        <a
          :href="
            route('auth0.login', {
              organization: selectedCompany.auth0_organization_id,
              company_id: selectedCompany.id
            })
          "
          class="border-2 border-gray-100 text-gray-100 rounded-md font-semibold text-xl px-6 py-4 uppercase text-center block hover:text-gray-100"
        >
          Single Sign-On
        </a>
      </div>

      <div
        v-if="hasDefaultLogin"
        class="flex flex-col gap-y-5"
      >
        <BreezeValidationErrors />

        <BreezeStatusMessage v-if="status">{{ status }}</BreezeStatusMessage>

        <div class="flex flex-col sm:flex-row justify-between items-center">
          <h3 class="text-3xl font-semibold">Sign-in</h3>
          <Link
            v-if="canResetPassword"
            :href="route('password.request')"
            class="font-semibold text-md text-gray-100 hover:text-gray-100 underline"
          >
            Forgot your password?
          </Link>
        </div>

        <form
          @submit.prevent="submit"
          class="flex flex-col gap-y-5"
        >
          <div>
            <BreezeLabel
              for="email"
              value="Email"
              class="sr-only"
            />
            <BreezeInput
              id="email"
              type="email"
              class="mt-1"
              v-model="form.email"
              required
              autofocus
              autocomplete="off"
              placeholder="Email address"
            />
          </div>

          <div>
            <BreezeLabel
              for="password"
              value="Password"
              class="sr-only"
            />
            <BreezeInput
              id="password"
              type="password"
              class="mt-1"
              v-model="form.password"
              required
              autocomplete="off"
              placeholder="Password"
            />
          </div>

          <div class="flex flex-col gap-y-3 sm:flex-row items-start justify-between">
            <label class="flex items-center">
              <BreezeCheckbox
                name="remember"
                v-model:checked="form.remember"
              />
              <span class="ml-2 text-md text-gray-100 font-semibold">Remember me</span>
            </label>
            <BreezeButton
              class="w-full sm:w-auto"
              :class="{ 'opacity-25': form.processing }"
              :disabled="form.processing"
            >
              Login
            </BreezeButton>
          </div>
        </form>
      </div>

      <div
        v-if="selectedCompany"
        class="text-md text-gray-100 font-semibold"
      >
        Logging in to <strong>{{ selectedCompany.name }}</strong>
        <br />
        <a
          class="text-gray-100 hover:text-gray-100 underline"
          href="#"
          @click.prevent="unselectCompany"
          >Switch organization</a
        >
      </div>
    </div>
  </transition>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 140ms;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.fade-enter-to,
.fade-leave-from {
  opacity: 1;
}
</style>

<script>
import BreezeButton from '@/Components/Auth/Button.vue'
import BreezeCheckbox from '@/Components/Auth/Checkbox.vue'
import BreezeGuestLayout from '@/Layouts/Guest.vue'
import BreezeInput from '@/Components/Auth/Input.vue'
import BreezeLabel from '@/Components/Auth/Label.vue'
import BreezeValidationErrors from '@/Components/Auth/ValidationErrors.vue'
import BreezeStatusMessage from '@/Components/Auth/StatusMessage.vue'
import { Head, Link } from '@inertiajs/vue3'

export default {
  layout: BreezeGuestLayout,

  components: {
    BreezeButton,
    BreezeCheckbox,
    BreezeInput,
    BreezeLabel,
    BreezeValidationErrors,
    BreezeStatusMessage,
    Head,
    Link
  },

  props: {
    canResetPassword: Boolean,
    status: String,
    companies: Array,
    sessionCompanyId: String
  },

  data() {
    return {
      enteredCompanyName: null,
      selectedCompanyId: null,
      selectCompanyError: null,

      form: this.$inertia.form({
        company_id: null,
        email: '',
        password: '',
        remember: false
      })
    }
  },

  created() {
    this.selectedCompanyId = this.sessionCompanyId
  },

  computed: {
    selectedCompany() {
      if (!this.selectedCompanyId) {
        return null
      }

      return _.find(this.companies, (company) => {
        return company.id === this.selectedCompanyId
      })
    },

    hasAuth0() {
      if (!this.selectedCompany) {
        return false
      }
      return this.selectedCompany.auth0_organization_id
    },

    hasDefaultLogin() {
      if (!this.selectedCompany) {
        return false
      }
      return this.selectedCompany.enable_default_login
    }
  },

  methods: {
    submitSelectCompany() {
      if (!this.enteredCompanyName) {
        return this.unselectCompany()
      }

      const enteredCompanyName = this.enteredCompanyName.replace(/\W/g, '').toLowerCase()

      let company = _.find(this.companies, (company) => {
        return (
          company.name.replace(/\W/g, '').toLowerCase() === enteredCompanyName ||
          company.slug.replace(/\W/g, '') === enteredCompanyName
        )
      })

      if (!company) {
        this.selectCompanyError = 'No match found'

        return
      }

      this.selectCompany(company.id)
    },

    selectCompany(companyId) {
      this.selectCompanyError = null
      this.selectedCompanyId = companyId
    },

    unselectCompany() {
      this.selectCompanyError = null
      this.selectedCompanyId = null
    },

    submit() {
      this.form.company_id = this.selectedCompanyId

      this.form.post(this.route('login'), {
        onFinish: () => this.form.reset('password')
      })
    }
  }
}
</script>
